import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { POSTransaction, HeldCart, HeldCartItem, POSDashboardStats, PaymentMethod } from '../types/pos';
import { CartItem } from '../types';
import { getAdminId as resolveAdminId } from '../utils/getAdminId';

interface CreateTransactionData {
  customer_id?: string;
  customer_name: string;
  payment_method: PaymentMethod;
  payment_status: 'paid' | 'unpaid' | 'internal';
  worker_id?: string;
  worker_name?: string;
  subtotal: number;
  tax_amount: number;
  total_amount: number;
  items_count: number;
  invoice_id?: string;
  reservation_id?: string;
  notes?: string;
}

export const usePOS = () => {
  const { user } = useAuth();
  const [transactions, setTransactions] = useState<POSTransaction[]>([]);
  const [heldCarts, setHeldCarts] = useState<HeldCart[]>([]);
  const [todayStats, setTodayStats] = useState<POSDashboardStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getAdminId = useCallback(() => {
    if (!user) return null;
    return resolveAdminId(user);
  }, [user]);

  const loadTodayTransactions = useCallback(async () => {
    if (!user) return;

    const adminId = getAdminId();
    if (!adminId) return;

    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const { data, error: fetchError } = await supabase
        .from('pos_transactions')
        .select('*')
        .eq('admin_id', adminId)
        .gte('created_at', today.toISOString())
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      setTransactions(data || []);

      const stats: POSDashboardStats = {
        todaySalesCount: data?.length || 0,
        todayRevenue: 0,
        pendingReservations: 0,
        expiringReservations: 0,
        heldCartsCount: heldCarts.length,
        cashTotal: 0,
        pinTotal: 0,
        loanTotal: 0,
        workerPickupTotal: 0
      };

      (data || []).forEach(txn => {
        stats.todayRevenue += txn.total_amount;
        switch (txn.payment_method) {
          case 'cash':
            stats.cashTotal += txn.total_amount;
            break;
          case 'pin':
            stats.pinTotal += txn.total_amount;
            break;
          case 'loan':
            stats.loanTotal += txn.total_amount;
            break;
          case 'worker_pickup':
            stats.workerPickupTotal += txn.total_amount;
            break;
        }
      });

      setTodayStats(stats);
    } catch (err) {
      console.error('Error loading transactions:', err);
    }
  }, [user, getAdminId, heldCarts.length]);

  const loadHeldCarts = useCallback(async () => {
    if (!user) return;

    const adminId = getAdminId();
    if (!adminId) return;

    try {
      const { data, error: fetchError } = await supabase
        .from('held_carts')
        .select('*')
        .eq('admin_id', adminId)
        .gt('expires_at', new Date().toISOString())
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      setHeldCarts(data || []);
    } catch (err) {
      console.error('Error loading held carts:', err);
    }
  }, [user, getAdminId]);

  useEffect(() => {
    if (user) {
      loadTodayTransactions();
      loadHeldCarts();
    }
  }, [user, loadTodayTransactions, loadHeldCarts]);

  const generateTransactionNumber = () => {
    const now = new Date();
    const timestamp = now.getTime().toString(36).toUpperCase();
    const random = Math.random().toString(36).substring(2, 6).toUpperCase();
    return `TXN-${timestamp}-${random}`;
  };

  const createTransaction = async (data: CreateTransactionData): Promise<POSTransaction | null> => {
    if (!user) return null;

    const adminId = getAdminId();
    if (!adminId) return null;

    setLoading(true);
    setError(null);

    try {
      const transactionNumber = generateTransactionNumber();

      const { data: transaction, error: insertError } = await supabase
        .from('pos_transactions')
        .insert({
          admin_id: adminId,
          transaction_number: transactionNumber,
          customer_id: data.customer_id || null,
          customer_name: data.customer_name,
          payment_method: data.payment_method,
          payment_status: data.payment_status,
          worker_id: data.worker_id || null,
          worker_name: data.worker_name || null,
          subtotal: data.subtotal,
          tax_amount: data.tax_amount,
          total_amount: data.total_amount,
          items_count: data.items_count,
          invoice_id: data.invoice_id || null,
          reservation_id: data.reservation_id || null,
          notes: data.notes || null,
          created_by: user.id
        })
        .select()
        .single();

      if (insertError) throw insertError;

      await loadTodayTransactions();
      return transaction;
    } catch (err) {
      setError('Failed to create transaction');
      console.error('Error creating transaction:', err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const holdCart = async (
    cartItems: CartItem[],
    customerName?: string,
    customerId?: string,
    notes?: string,
    taxRate: number = 21
  ): Promise<HeldCart | null> => {
    if (!user) return null;

    const adminId = getAdminId();
    if (!adminId) return null;

    setLoading(true);
    setError(null);

    try {
      const items: HeldCartItem[] = cartItems.map(item => ({
        id: item.id,
        name: item.name,
        category: item.category,
        quantity: item.cartQuantity,
        unit_price: item.sell_price || item.unit_price,
        line_total: item.lineTotal,
        color_id: item.color_id,
        size_id: item.size_id
      }));

      const subtotal = items.reduce((sum, item) => sum + item.line_total, 0);
      const taxAmount = subtotal * (taxRate / 100);
      const totalAmount = subtotal + taxAmount;

      const expiresAt = new Date();
      expiresAt.setHours(expiresAt.getHours() + 24);

      const { data: heldCart, error: insertError } = await supabase
        .from('held_carts')
        .insert({
          admin_id: adminId,
          created_by: user.id,
          customer_id: customerId || null,
          customer_name: customerName || null,
          cart_items: items,
          subtotal,
          tax_amount: taxAmount,
          total_amount: totalAmount,
          notes: notes || null,
          expires_at: expiresAt.toISOString()
        })
        .select()
        .single();

      if (insertError) throw insertError;

      await loadHeldCarts();
      return heldCart;
    } catch (err) {
      setError('Failed to hold cart');
      console.error('Error holding cart:', err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const restoreCart = (heldCart: HeldCart): CartItem[] => {
    return heldCart.cart_items.map(item => ({
      id: item.id,
      name: item.name,
      category: item.category,
      unit_price: item.unit_price,
      quantity: 999,
      cartQuantity: item.quantity,
      lineTotal: item.line_total,
      color_id: item.color_id,
      size_id: item.size_id,
      sell_price: item.unit_price
    }));
  };

  const deleteHeldCart = async (id: string): Promise<boolean> => {
    try {
      const { error: deleteError } = await supabase
        .from('held_carts')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;

      setHeldCarts(prev => prev.filter(c => c.id !== id));
      return true;
    } catch (err) {
      console.error('Error deleting held cart:', err);
      return false;
    }
  };

  const getRecentTransactions = useCallback((limit: number = 10): POSTransaction[] => {
    return transactions.slice(0, limit);
  }, [transactions]);

  const getTransactionsByMethod = useCallback((method: PaymentMethod): POSTransaction[] => {
    return transactions.filter(t => t.payment_method === method);
  }, [transactions]);

  return {
    transactions,
    heldCarts,
    todayStats,
    loading,
    error,
    createTransaction,
    holdCart,
    restoreCart,
    deleteHeldCart,
    getRecentTransactions,
    getTransactionsByMethod,
    refreshTransactions: loadTodayTransactions,
    refreshHeldCarts: loadHeldCarts
  };
};
